/**
 * Who owns which 2026 pick, in one place.
 *
 * The ledger is `pick-notes-2026.json` and five screens read it. That was tolerable while they
 * only wanted to PRINT it; it stopped being tolerable when two of them started reasoning with it,
 * because Draft tools was not reading it at all — it handed the keeper resolver a flat 1..16 for
 * every manager, so a GM who had traded his 4th was still projected keeping somebody at a 4th he
 * no longer held, and the slate that came out was one slot too generous. The draftboard had the
 * right answer the whole time, in a private function.
 *
 * So the derivation lives here and both callers use it. A projection that disagrees with the
 * board about who owns what is not a projection of that board.
 */

import pickNotes from '../data/pick-notes-2026.json';
import { ownershipFrom } from './board.js';

/**
 * Team name -> manager. Passed in rather than imported, because the mapping is not static: a seat
 * change made in the app has to reach this, and the screens already hold the live lookup.
 */
export type GmOfTeam = (team: string) => string | undefined;

/** Round ownership after every trade in the ledger, keyed `${round}:${originalSeat}` -> holder. */
export function ownership2026(gmOfTeam: GmOfTeam): Map<string, string> {
  const owned = (pickNotes as {
    ownedPicks: Record<string, Array<{ round: number; from: string }>>;
  }).ownedPicks;
  return ownershipFrom(Object.fromEntries(
    Object.entries(owned).map(([team, picks]) => [
      gmOfTeam(team) ?? team.toUpperCase(),
      picks.map((p) => ({ round: p.round, from: gmOfTeam(p.from) ?? p.from.toUpperCase() })),
    ]),
  ));
}

/**
 * The rounds this GM can actually charge a keeper to.
 *
 * A round appears once per pick he holds in it, so a manager holding two 12ths gets `[12, 12]` —
 * which is the whole point, since two keepers can then be charged to round 12. His own seat counts
 * unless somebody else holds it; everything he has taken off another seat counts as well.
 */
export function ownedRoundsFor(
  gm: string, ownership: ReadonlyMap<string, string>, rounds: number,
): number[] {
  const me = gm.toUpperCase();
  const owned: number[] = [];
  for (let r = 1; r <= rounds; r += 1) {
    if ((ownership.get(`${r}:${me}`) ?? me) === me) owned.push(r);
    for (const [key, holder] of ownership) {
      const [round, seat] = key.split(':');
      if (Number(round) === r && holder === me && seat !== me) owned.push(r);
    }
  }
  return owned;
}

/**
 * Every 2026 pick a GM holds, each labelled with the seat it came off.
 *
 * `ownedRoundsFor` answers "how many keepers can he charge to round 12" and returns `[12, 12]`,
 * which is right for the calculator and useless for a trade: the two twelfths are different
 * objects and the trade has to say WHICH one moves. The 19 Nov 2025 deal is the case — it left
 * John with two ninths and two elevenths, and a trade that just says "2026 9th" leaves the board
 * unable to work out whose seat emptied.
 *
 * Ordered by round, then own-seat first, because "my own 9th" is the one a GM means when he does
 * not say.
 */
export interface Holding { round: number; from: string }

export function holdingsFor(
  gm: string, ownership: ReadonlyMap<string, string>, rounds: number,
): Holding[] {
  const me = gm.toUpperCase();
  const out: Holding[] = [];
  for (let r = 1; r <= rounds; r += 1) {
    if ((ownership.get(`${r}:${me}`) ?? me) === me) out.push({ round: r, from: me });
    for (const [key, holder] of ownership) {
      const [round, seat] = key.split(':');
      if (Number(round) === r && holder === me && seat !== me) out.push({ round: r, from: seat! });
    }
  }
  return out;
}

/** "Ryan's own" / "from John" — only ever shown where a round holds more than one. */
export function originLabel(h: Holding, gm: string, name: (g: string) => string): string {
  return h.from.toUpperCase() === gm.toUpperCase() ? `${name(gm)}’s own` : `from ${name(h.from)}`;
}
